import React from 'react';

/**
 * DockCard Component
 * Individual card for each dock/gate with glass-themed action buttons
 */
const DockCard = ({ dock, state, color, onManage, onViewDetails }) => {
    return (
        <div
            className="card-premium glow-theme dock-card transition-all duration-300 hover:scale-[1.005] hover:shadow-lg"
            style={{
                padding: '1.25rem 1.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}
        >
            {/* Info Section */}
            <div className="flex items-center gap-15 flex-1">
                {/* Status Badge - Gradient Background Like Rampa Detay */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '50px',
                        background: `linear-gradient(135deg, ${color}90 0%, ${color}70 100%)`,
                        border: `2px solid ${color}`,
                        boxShadow: `0 0 20px ${color}40`,
                        minWidth: '140px',
                        justifyContent: 'center'
                    }}
                >
                    {/* Status Dot - White */}
                    <div
                        style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            background: '#ffffff',
                            boxShadow: '0 0 10px rgba(255, 255, 255, 0.8)'
                        }}
                    />
                    {/* Status Text - White */}
                    <span
                        style={{
                            color: '#ffffff',
                            fontWeight: '700',
                            fontSize: '0.95rem',
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                        }}
                    >
                        {state.status === 'FREE' ? 'MÜSAİT' :
                            state.status === 'OCCUPIED' ? 'DOLU' :
                                state.status === 'RESERVED' ? 'REZERVE' : 'BAKIM'}
                    </span>
                </div>

                {/* Gate Info & Progress - Aligned */}
                <div className="flex flex-col gap-3 flex-1">
                    {/* Gate Name - Theme Color, Uppercase */}
                    <h3
                        className="text-2xl font-bold m-0 tracking-wide"
                        style={{
                            color: 'var(--text-primary)',
                            textTransform: 'uppercase',
                            fontWeight: '800'
                        }}
                    >
                        {dock.name}
                    </h3>

                    {/* Stats Text - Total, Today, Completed */}
                    <div className="flex items-center gap-3 text-sm font-mono">
                        <span className="text-secondary font-medium">
                            Total: <span className="text-primary font-bold">{state.totalCount || 0}</span>
                        </span>
                        <span className="text-secondary">|</span>
                        <span className="text-secondary font-medium">
                            Today: <span className="text-primary font-bold">{state.plannedCount || 0}</span>
                        </span>
                        <span className="text-secondary">|</span>
                        <span className="text-secondary font-medium">
                            Completed: <span className="text-primary font-bold">{state.completedCount || 0}</span>
                        </span>
                    </div>

                    {/* Progress Bar - Full Width Below Stats */}
                    <div className="flex items-center gap-3 w-full">
                        {/* Bar - Red Background for 'Remaining/Not Started' */}
                        <div
                            style={{
                                flex: 1,
                                height: '24px',
                                background: 'rgba(239, 68, 68, 0.2)', // Red tint background
                                borderRadius: '12px',
                                overflow: 'hidden',
                                border: '2px solid rgba(255, 255, 255, 0.1)', // Subtler border
                                position: 'relative',
                                boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.3)'
                            }}
                        >
                            {/* Red "Remaining" Background Layer (if we want solid red for empty) or just use the container bg as red */}
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    background: '#ef4444', // Red for remaining
                                    opacity: 0.6
                                }}
                            />

                            {/* Progress Fill - Green for 'Completed' */}
                            <div
                                style={{
                                    height: '100%',
                                    width: `${state.plannedCount > 0 ? ((state.completedCount || 0) / state.plannedCount * 100) : 0}%`,
                                    background: 'linear-gradient(90deg, #22c55e 0%, #4ade80 100%)', // Green Gradient
                                    transition: 'width 0.5s ease',
                                    boxShadow: state.completedCount > 0 ? '0 0 15px rgba(34, 197, 94, 0.6)' : 'none',
                                    borderRadius: '10px', // Slightly less rounded to fit
                                    position: 'relative',
                                    zIndex: 10
                                }}
                            />
                        </div>

                        {/* Percentage Text - Theme Color */}
                        <span
                            className="font-mono font-bold text-sm"
                            style={{
                                color: 'var(--text-primary)',
                                minWidth: '50px',
                                textAlign: 'right'
                            }}
                        >
                            {state.plannedCount > 0
                                ? `${Math.round(((state.completedCount || 0) / state.plannedCount) * 100)}%`
                                : '0%'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Actions Section - Theme Buttons */}
            <div className="flex items-center gap-4 pr-4">
                {/* Rampa Yönetimi Button - Subtle Glass */}
                <button
                    className="glass-button group"
                    onClick={onManage}
                    style={{
                        padding: '0.875rem 2rem',
                        borderRadius: '50px',
                        background: 'var(--glass-bg, rgba(255, 255, 255, 0.05))',
                        border: '2px solid var(--glass-border, rgba(255, 255, 255, 0.2))',
                        backdropFilter: 'blur(10px)',
                        color: 'var(--text-primary)',
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'pointer',
                        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--glass-bg-hover, rgba(255, 255, 255, 0.1))';
                        e.currentTarget.style.borderColor = 'var(--glass-border-hover, rgba(255, 255, 255, 0.3)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--glass-bg, rgba(255, 255, 255, 0.05))';
                        e.currentTarget.style.borderColor = 'var(--glass-border, rgba(255, 255, 255, 0.2))';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 10px rgba(0, 0, 0, 0.1)';
                    }}
                >
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        className="transition-transform duration-300 group-hover:rotate-180"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                    </svg>
                    Rampa Yönetimi
                </button>

                {/* Rampa Detay Button - Status Color Accent */}
                <button
                    className="glass-button-primary group"
                    onClick={onViewDetails}
                    style={{
                        padding: '0.875rem 2rem',
                        borderRadius: '50px',
                        background: `linear-gradient(135deg, ${color}90 0%, ${color}70 100%)`,
                        border: `2px solid ${color}`,
                        backdropFilter: 'blur(10px)',
                        color: '#ffffff',
                        fontSize: '0.95rem',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'pointer',
                        boxShadow: `0 0 20px ${color}40, 0 4px 10px rgba(0, 0, 0, 0.15)`,
                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = color;
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = `0 0 30px ${color}60, 0 8px 20px rgba(0, 0, 0, 0.2)`;
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = `linear-gradient(135deg, ${color}90 0%, ${color}70 100%)`;
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = `0 0 20px ${color}40, 0 4px 10px rgba(0, 0, 0, 0.15)`;
                    }}
                >
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        className="transition-transform duration-300 group-hover:scale-110"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    Rampa Detay
                </button>
            </div>
        </div>
    );
};

export default DockCard;
